/**
 * Delivery Filters Component
 * Provides filtering options for the delivery list
 */

import { useState } from 'react';
import type { DeliveryFilterParams } from '@/services/commerce/deliveryService';

interface DeliveryFiltersProps {
  onFiltersChange: (filters: DeliveryFilterParams) => void;
  onSearch: (query: string) => void;
}

// Transport-style status options
const STATUSES = [
  { value: 'REQUEST_CREATED', label: 'Request Created' },
  { value: 'BROADCASTING', label: 'Broadcasting' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'PICKED_UP', label: 'Picked Up' },
  { value: 'ARRIVED_DROP', label: 'Arrived at Drop' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'PAYMENT_PENDING', label: 'Payment Pending' },
  { value: 'PAYMENT_RECEIVED', label: 'Payment Received' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export function DeliveryFilters({ onFiltersChange, onSearch }: DeliveryFiltersProps) {
  const [status, setStatus] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus);
    onFiltersChange({ status: newStatus || undefined });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onSearch(query);
  };

  const clearFilters = () => {
    setStatus('');
    setSearchQuery('');
    onFiltersChange({});
    onSearch('');
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow mb-6">
      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by Transport ID (TR-xxx), Order ID, customer, address, driver..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <svg className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap gap-4">
        {/* Status Filter */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Filters Button */}
        <div className="flex items-end">
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
}
